/** One part's byte range within the source file, derived from the backend's own part size. */
export interface PartPlan {
	partNumber: number
	start: number
	/** Exclusive, as `Blob.slice` takes it. */
	end: number
	size: number
}

/**
 * The part boundaries for one session, computed from the backend-trusted
 * `declaredFileSize`/`partSize` — never from the local file's own size.
 *
 * An exact multiple produces no zero-byte trailing part, because `partCount`
 * itself is `ceil(size / partSize)`: the last part is the exact remainder, and
 * for an exact multiple that remainder is one full part.
 */
export function planParts(declaredFileSize: number, partSize: number, partCount: number): PartPlan[] {
	const plan: PartPlan[] = []
	for (let partNumber = 1; partNumber <= partCount; partNumber++) {
		const start = (partNumber - 1) * partSize
		const end = Math.min(start + partSize, declaredFileSize)
		plan.push({ partNumber, start, end, size: end - start })
	}
	return plan
}

/**
 * Aggregate byte progress over the trusted session size.
 *
 * Completed parts and in-flight parts are tracked separately so a retry cannot
 * double-count: a part that restarts simply replaces its own in-flight figure,
 * and progress is allowed to move backward when authoritative reconciliation
 * reclassifies bytes it had optimistically counted.
 */
export class PartProgress {
	private completed = new Map<number, number>()
	private inFlight = new Map<number, number>()

	constructor(private readonly declaredFileSize: number) {}

	/** Records parts S3 already holds — the resume baseline, and the only thing that survives reconciliation. */
	markCompleted(partNumber: number, bytes: number): void {
		this.inFlight.delete(partNumber)
		this.completed.set(partNumber, bytes)
	}

	/** Drops a part's contribution entirely: reconciliation says those bytes are not valid after all. */
	invalidate(partNumber: number): void {
		this.completed.delete(partNumber)
		this.inFlight.delete(partNumber)
	}

	trackInFlight(partNumber: number, bytes: number): void {
		this.inFlight.set(partNumber, bytes)
	}

	/** 0-100, clamped: a transfer that reports more bytes than the part holds must never push the tile past 100. */
	percent(): number {
		if (this.declaredFileSize <= 0) return 0
		const transferred = sum(this.completed) + sum(this.inFlight)
		return Math.min(100, Math.round((transferred / this.declaredFileSize) * 100))
	}
}

function sum(values: Map<number, number>): number {
	let total = 0
	for (const value of values.values()) total += value
	return total
}
